import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConnectionState } from 'livekit-client';
import StreamLayoutCanvas from '../../components/StreamLayoutCanvas';
import styles from './StreamLayoutPage.module.css';
import { Scene, UserStudio } from '../../features/api/apiSlice';
import { useSocketApi } from '../../hooks/useSocketApi';
import { SocketContext } from '../../providers/SocketProvider/SocketProvider';
import { parseStudioParams } from '../../helpers/parseStudioParams';
import { getStreamOrientation } from '../../utils/streamOrientation';

export interface StudioLayout {
  studio_token: string;
  studio_id: number;
}

const StreamLayoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const url = searchParams.get('url') || '';
  const token = searchParams.get('token') || '';
  const rawLayout = searchParams.get('layout');
  const viewOnly = searchParams.get('viewOnly') === 'true';
  const [layout, setLayout] = useState<StudioLayout | undefined>(undefined);
  const [currentScene, setCurrentScene] = useState<Scene>();
  const [currenStudio, setCurrentStudio] = useState<UserStudio>();
  const { getStreamLayoutPageClasses, getStreamLayoutPlayerClasses } = getStreamOrientation(currenStudio);

  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState | 'disconnected'>('disconnected');
  const { connectToStudioSocket, studioSocketIdentifier, webSocket } = useContext(SocketContext);

  useSocketApi({
    studioId: layout?.studio_id,
    onStudioSceneUpdated: ({ studio_scene }) => {
      setCurrentScene(studio_scene);
    },
    onStudioUpdated: ({studio}) => {
      setCurrentStudio(studio);
      setCurrentScene(studio.current_scene);
    }
  });

  useEffect(() => {
    const parsedLayout = parseStudioParams(rawLayout);
    setLayout(parsedLayout);
  }, [rawLayout]);

  useEffect(() => {
    if (!layout?.studio_id) {
      return;
    }
    connectToStudioSocket(layout?.studio_id);
  }, [layout, studioSocketIdentifier, webSocket?.readyState]);

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleConnectionStateChange = (state: ConnectionState | 'disconnected') => {
    setConnectionState(state);
  };

  if (!url || !token) {
    return (
      <div className={styles['stream-layout-page__error']}>
        <h2 className={styles['stream-layout-page__error-title']}>Ошибка</h2>
        <p className={styles['stream-layout-page__error-message']}>
          Отсутствуют необходимые параметры (url, token)
        </p>
      </div>
    );
  }

  return (
    <div className={getStreamLayoutPageClasses(styles['stream-layout-page'])}>
      {viewOnly && (
        <div className={styles['stream-layout-page__view-only-banner']}>
          <span>Режим просмотра</span>
          <span className={styles['stream-layout-page__view-only-layout']}>
            Шаблон: {rawLayout}
          </span>
          <span className={styles['stream-layout-page__connection-state']}>
            Состояние: {
              connectionState === ConnectionState.Connected ? 'Подключено' :
                connectionState === ConnectionState.Connecting ? 'Подключение...' :
                  connectionState === ConnectionState.Reconnecting ? 'Переподключение...' :
                    'Отключено'
            }
          </span>
        </div>
      )}

      {error && (
        <div className={styles['stream-layout-page__error-overlay']}>
          <div className={styles['stream-layout-page__error-content']}>
            <h3>Ошибка подключения</h3>
            <p>{error}</p>
            <button
              className={styles['stream-layout-page__reload-button']}
              onClick={() => window.location.reload()}
            >
              Перезагрузить страницу
            </button>
          </div>
        </div>
      )}

      <StreamLayoutCanvas
        url={url}
        token={token}
        className={getStreamLayoutPlayerClasses(styles["stream-layout-page__player"])}
        layout={layout}
        currentTemplate={currentScene?.current_template}
        background={currentScene?.background}
        logo={currentScene?.logo}
        isRecordingMode={true}
        onError={handleError}
        onConnectionStateChange={handleConnectionStateChange}
      />
    </div>
  );
};

export default StreamLayoutPage;
